import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const services = [
  { name: 'El-installatør', href: '/vi-tilbyder/el-installatoer' },
  { name: 'Kabler', href: '/vi-tilbyder/kabler' },
  { name: 'Elektriker', href: '/vi-tilbyder/elektriker' },
  { name: 'Fagentrepriser', href: '/vi-tilbyder/fagentrepriser' },
  { name: 'Installationer', href: '/vi-tilbyder/installationer' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b',
        isScrolled
          ? 'bg-black/90 backdrop-blur-md border-white/10 py-5'
          : 'bg-transparent border-transparent py-8'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex flex-col items-start leading-none group">
            <span className="text-5xl font-bold text-[#C1121F] tracking-tighter group-hover:scale-105 transition-transform">L1</span>
            <span className="text-xs uppercase tracking-[0.2em] text-white font-medium">El-installatør</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-sm font-bold text-white hover:opacity-70 transition-colors uppercase tracking-wider">Forside</Link>
            
            <div className="relative group">
              <button className="flex items-center space-x-1 text-sm font-bold text-white hover:opacity-70 transition-colors uppercase tracking-wider">
                <span>Vi tilbyder</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-56 bg-[#0F0F0F] border border-white/10 rounded-none overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0">
                {services.map((service) => (
                  <Link
                    key={service.href}
                    to={service.href}
                    className="block px-4 py-3 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors uppercase tracking-wider font-bold"
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/om-os" className="text-sm font-bold text-white hover:opacity-70 transition-colors uppercase tracking-wider">Om os</Link>
            <Link to="/kontakt" className="px-5 py-2.5 bg-[#C1121F] text-white text-sm font-bold rounded-none hover:bg-[#A00F1A] transition-all transform hover:-translate-y-0.5 active:translate-y-0 uppercase tracking-wider">
              Kontakt os
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(true)}
              className="text-white p-2"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Menu */}
      <div
        className={cn(
          'fixed inset-0 z-[60] md:hidden transition-opacity duration-300',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
        
        {/* Sidebar */}
        <div
          className={cn(
            'absolute top-0 right-0 bottom-0 w-[80%] max-w-sm bg-[#0F0F0F] shadow-2xl transition-transform duration-300 ease-out flex flex-col',
            isOpen ? 'translate-x-0' : 'translate-x-full'
          )}
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <Link to="/" onClick={() => setIsOpen(false)} className="flex flex-col items-start leading-none">
              <span className="text-4xl font-bold text-[#C1121F] tracking-tighter">L1</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-white font-medium">El-installatør</span>
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white p-2 hover:bg-white/5 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Sidebar Links */}
          <div className="flex-1 overflow-y-auto py-8 px-6 space-y-6">
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className="block text-xl font-bold text-white uppercase tracking-wider hover:text-[#C1121F] transition-colors"
            >
              Forside
            </Link>
            
            <div className="space-y-4">
              <button 
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className="flex items-center justify-between w-full text-xl font-bold text-white uppercase tracking-wider hover:text-[#C1121F] transition-colors"
              >
                <span>Vi tilbyder</span>
                <ChevronDown className={cn("w-5 h-5 transition-transform duration-200", isServicesOpen && "rotate-180")} />
              </button>
              
              {isServicesOpen && (
                <div className="grid gap-4 pl-4 border-l border-white/10">
                  {services.map((service) => (
                    <Link
                      key={service.href}
                      to={service.href}
                      onClick={() => setIsOpen(false)}
                      className="block text-base font-bold text-gray-400 hover:text-white uppercase tracking-wider"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/om-os"
              onClick={() => setIsOpen(false)}
              className="block text-xl font-bold text-white uppercase tracking-wider hover:text-[#C1121F] transition-colors"
            >
              Om os
            </Link>
          </div>

          {/* Sidebar Footer */}
          <div className="p-6 border-t border-white/10">
            <Link
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className="block w-full py-4 bg-[#C1121F] text-white text-center text-sm font-bold uppercase tracking-wider hover:bg-[#A00F1A] transition-colors"
            >
              Kontakt os
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
